import { serviceQualityContractualDeviationEnumsAffectedSLAMetricOptionEnum } from '@/gen/types/ServiceQualityContractualDeviationEnumsAffectedSLAMetricOption';
import { serviceQualityContractualDeviationEnumsIncidentAffectsACustomerOptionEnum } from '@/gen/types/ServiceQualityContractualDeviationEnumsIncidentAffectsACustomerOption';
import { yesNoOptionEnum } from '@/gen/types/YesNoOption';

import { whenAll, whenPathEquals, whenPathIncludes } from './conditions';
import type { IncidentFieldRule } from './types';

const hasPotentialOrActualSlaBreach = whenPathEquals('potentialOrActualSLABreach', yesNoOptionEnum.Yes);
const affectedSlaMetricIncludesOther = whenPathIncludes(
    'affectedSLAMetric',
    serviceQualityContractualDeviationEnumsAffectedSLAMetricOptionEnum.Other,
);

export const serviceQualityContractualDeviationFieldRules: IncidentFieldRule[] = [
    {
        key: 'subCategory',
        label: 'What kind of service quality or contractual issue is this?',
        description: 'Select the type of service quality or contractual deviation.',
        order: 90,
    },
    {
        key: 'potentialOrActualSLABreach',
        label: 'Is there a potential or actual SLA breach?',
        description: 'Select Yes if a service level agreement may have been breached.',
        order: 100,
    },
    {
        key: 'affectedSLAMetric',
        label: 'Which SLA metric is affected?',
        description: 'Select the SLA metric impacted, e.g. uptime, response time, availability.',
        order: 110,
        visibleWhen: hasPotentialOrActualSlaBreach,
        requiredWhen: hasPotentialOrActualSlaBreach,
    },
    {
        key: 'affectedSLAMetricExplanation',
        label: 'Affected SLA metric explanation',
        kind: 'textarea',
        order: 120,
        visibleWhen: whenAll(hasPotentialOrActualSlaBreach, affectedSlaMetricIncludesOther),
        requiredWhen: whenAll(hasPotentialOrActualSlaBreach, affectedSlaMetricIncludesOther),
    },
    {
        key: 'descriptionOfTheSuspectedSLADeviation',
        label: 'Please describe the SLA deviation',
        description: 'Explain what deviated from the agreed SLA, including timeline and scope.',
        kind: 'textarea',
        order: 130,
        visibleWhen: hasPotentialOrActualSlaBreach,
        requiredWhen: hasPotentialOrActualSlaBreach,
    },
    {
        key: 'missedContractualObligation',
        label: 'Was a contractual obligation missed?',
        description: 'Select Yes if a specific contractual commitment was not fulfilled.',
        order: 140,
    },
    {
        key: 'missedContractualObligationExplanation',
        label: 'Please describe the affected contractual obligation',
        description: 'Describe the specific contractual clause or obligation affected.',
        kind: 'textarea',
        order: 150,
        visibleWhen: whenPathEquals('missedContractualObligation', yesNoOptionEnum.Yes),
        requiredWhen: whenPathEquals('missedContractualObligation', yesNoOptionEnum.Yes),
    },
    {
        key: 'deviationIdentifiedInDocumentation',
        label: 'Is there a deviation in documentation, procedures or standards?',
        description: 'Select Yes if any documented process or standard was not followed.',
        order: 160,
    },
    {
        key: 'deviationIdentifiedInDocumentationExplanation',
        label: 'Which document or procedure is affected?',
        description: 'Enter the name or reference of the affected document or procedure.',
        kind: 'textarea',
        order: 170,
        visibleWhen: whenPathEquals('deviationIdentifiedInDocumentation', yesNoOptionEnum.Yes),
        requiredWhen: whenPathEquals('deviationIdentifiedInDocumentation', yesNoOptionEnum.Yes),
    },
    {
        key: 'incidentAffectsACustomer',
        label: 'Does this affect a customer?',
        description: 'Select Yes if any customer was impacted by this deviation.',
        order: 180,
    },
    {
        key: 'affectedCustomer',
        label: 'Affected customer',
        order: 190,
        visibleWhen: whenPathEquals(
            'incidentAffectsACustomer',
            serviceQualityContractualDeviationEnumsIncidentAffectsACustomerOptionEnum.Yes,
        ),
        requiredWhen: whenPathEquals(
            'incidentAffectsACustomer',
            serviceQualityContractualDeviationEnumsIncidentAffectsACustomerOptionEnum.Yes,
        ),
    },
];
